import React, { useEffect, useState } from 'react';
import TheatreCard from '../Components/TheatreCard';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import UpdateModal from '../Components/UpdateModal';
import BookingStepOne from '../Components/BookingStepOne';
import { GetApiHandler, apiBaseURL } from '../utils/Api';

const getTodayDateString = () => {
  const now = new Date();
  return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');
};

const getInitialFilters = () => {
  const params = new URLSearchParams(window.location.search);

  return {
    city_id: params.get('city_id') || '',
    location_id: params.get('location_id') || '',
    show_date: params.get('show_date') || getTodayDateString(),
    city_name: params.get('city_name') || '',
    location_name: params.get('location_name') || '',
  };
};

const formatDisplayDate = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const HomeScreen = () => {
  const initialFilters = getInitialFilters();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSelectionBar, setShowSelectionBar] = useState(false);
  const [showBookingStep, setShowBookingStep] = useState(false);
  const [theatres, setTheatres] = useState([]);
  const [filters, setFilters] = useState({
    city_id: initialFilters.city_id,
    location_id: initialFilters.location_id,
    show_date: initialFilters.show_date,
  });
  const [selectedCityName, setSelectedCityName] = useState(initialFilters.city_name);
  const [selectedLocationName, setSelectedLocationName] = useState(initialFilters.location_name);
  const [selectedTheatre, setSelectedTheatre] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const currentCity = selectedLocationName || selectedCityName || theatres[0]?.location || 'Cineflix';
  const currentDate = formatDisplayDate(filters.show_date) || 'Choose a date';

  const handleTimeSlotSelect = (theatre, slot) => {
    setSelectedTheatre(theatre);
    setSelectedSlot(slot);
    setShowSelectionBar(true);
  };

  useEffect(() => {
    const assetBase = apiBaseURL.replace(/\/api\/v1$/, '');
    const RUPEE = '\u20b9';
    const now = new Date();
    const todayDateString = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');

    const toMoney = (value) => {
      if (value === null || value === undefined || value === '') return '';
      const num = Number(value);
      if (Number.isNaN(num)) return '';
      return `${RUPEE}${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    };

    const normalizeAssetPath = (val) => {
      if (!val) return '';
      return val.startsWith('http') ? val : `${assetBase}/${val}`;
    };

    const makeMapUrl = (val) => {
      if (!val) return '';
      if (val.startsWith('http')) return val;
      if (val.includes(',')) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(val)}`;
      }
      return '';
    };

    const formatSlot = (slot) => {
      const fmt = (t) => (t && t.length >= 5 ? t.slice(0, 5) : t || '');
      const slotDate = slot.show_date || filters.show_date;
      const [hours = '0', minutes = '0'] = String(slot.start_time || '').split(':');
      const slotStart = new Date(now);

      slotStart.setHours(Number(hours), Number(minutes), 0, 0);

      const isPastSlotToday = slotDate === todayDateString && slotStart <= now;

      return {
        id: slot.id,
        label: `${fmt(slot.start_time)} - ${fmt(slot.end_time)}`,
        disabled: slot.is_available === false || isPastSlotToday,
        raw: slot,
      };
    };

    const fetchTheatres = async () => {
      try {
        const payload = await GetApiHandler(filters, 'theaters');
        console.log('theaters response', payload);

        const list = Array.isArray(payload?.data) ? payload.data : [];
        const mapped = list.map((item, idx) => {
          const available = Number(item.available_slots_count) || 0;
          const imagePaths = Array.isArray(item.image_paths)
            ? item.image_paths.map(normalizeAssetPath).filter(Boolean)
            : [];
          const primaryImage =
            imagePaths[0] ||
            normalizeAssetPath(item.image_path);

          return {
            id: item.id || `remote-${idx}`,
            name: item.name || 'Theatre',
            location: item.city_name || selectedCityName || 'City',
            rating: 4.9,
            image: primaryImage,
            imagePaths: imagePaths.length
              ? imagePaths
              : primaryImage
              ? [primaryImage]
              : [],
            videoLinks: item.video_link ? [{ label: 'Video', url: item.video_link }] : [],
            mapsUrl: makeMapUrl(item.map_location),
            foodUrl: normalizeAssetPath(item.food_menu_image_path),
            slotsAvailableText: available ? `${available} Slot${available > 1 ? 's' : ''} Available` : 'No slots',
            slotsStatus: available > 0 ? 'available' : 'unavailable',
            features: [
              item.max_capacity ? `Max ${item.max_capacity} People` : null,
              item.cancellation_policy?.name || null,
              item.base_number_of_people && item.per_person_cost
                ? `Includes ${item.base_number_of_people} people, ${RUPEE}${Number(
                    item.per_person_cost
                  ).toLocaleString('en-IN', { maximumFractionDigits: 0 })} per extra`
                : null,
            ].filter(Boolean),
            timeSlots: Array.isArray(item.slots) ? item.slots.map(formatSlot) : [],
            priceMain: toMoney(item.booking_fee_amount),
            bookingFeeAmount: Number(item.booking_fee_amount) || 0,
            advancePayableAmount: Number(item.advance_payable_amount) || 0,
            priceSuffix: item.base_number_of_people
              ? `for up to ${item.base_number_of_people} people`
              : '',
            priceExtra:
              item.per_person_cost && item.base_number_of_people
                ? `+ ${toMoney(item.per_person_cost)}/person after ${item.base_number_of_people} people`
                : '',
            raw: item,
          };
        });

        setTheatres(mapped);

        if (!selectedCityName) {
          const firstCity = mapped.find((item) => item.location)?.location;
          if (firstCity) {
            setSelectedCityName(firstCity);
          }
        }
      } catch (error) {
        console.error('Failed to load theatres', error);
      }
    };

    fetchTheatres();
  }, [filters, selectedCityName]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.city_id) params.set('city_id', filters.city_id);
    if (filters.location_id) params.set('location_id', filters.location_id);
    if (filters.show_date) params.set('show_date', filters.show_date);
    if (selectedCityName) params.set('city_name', selectedCityName);
    if (selectedLocationName) params.set('location_name', selectedLocationName);

    const nextUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, [filters, selectedCityName, selectedLocationName]);

  const handleUpdateApply = ({ cityId, locationId, showDate, cityName, locationName }) => {
    setFilters({
      city_id: cityId || '',
      location_id: locationId || '',
      show_date: showDate || '',
    });
    setSelectedCityName(cityName || selectedCityName);
    setSelectedLocationName(locationName || '');
    setSelectedTheatre(null);
    setSelectedSlot(null);
    setShowSelectionBar(false);
    setShowBookingStep(false);
    setIsModalOpen(false);
  };

  const formatINR = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const num = Number(val);
    if (Number.isNaN(num)) return '';
    return `\u20b9${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  return (
    <div className={`page-shell${showSelectionBar ? ' with-selection-bar' : ''}`} id="top">
      <Header />

      <div className="container">
        <a href="#back" className="back-link">
          <i className="bi bi-arrow-left" />
          Back
        </a>
      </div>

      <div className="location-bar">
        <div className="container location-bar-inner">
          <div className="location-meta">
            <h2 className="location-title">{currentCity}</h2>
            <span className="location-date">{currentDate}</span>
          </div>
          <button className="btn-update" type="button" onClick={() => setIsModalOpen(true)}>
            Update
          </button>
        </div>
      </div>

      <main className="container page-content">
        <h3 className="section-title">
          {theatres.length} private theatres in {currentCity}
        </h3>

        <div className="theatre-grid">
          {theatres.map((theatre) => {
            const isSelected = selectedTheatre?.id === theatre.id;
            const slotPrice =
              isSelected && selectedSlot?.raw?.base_price ? formatINR(selectedSlot.raw.base_price) : null;
            return (
              <TheatreCard
                key={theatre.id}
                theatre={theatre}
                onSelectTimeSlot={(slot) => handleTimeSlotSelect(theatre, slot)}
                showPrice={isSelected && !!selectedSlot}
                priceOverride={slotPrice}
              />
            );
          })}
        </div>
      </main>

      <Footer />
      <UpdateModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialCityId={filters.city_id}
        initialLocationId={filters.location_id}
        initialDate={filters.show_date}
        onApply={handleUpdateApply}
      />
      {showSelectionBar && !showBookingStep && (
        <div className="selection-bar">
          <div className="selection-bar-text">
            {selectedTheatre?.name ? `${selectedTheatre.name}: ` : ''}
            {selectedSlot?.raw?.base_price ? `Slot price ${formatINR(selectedSlot.raw.base_price)}. ` : ''}
            {selectedTheatre?.advancePayableAmount
              ? `Pay only ${formatINR(selectedTheatre.advancePayableAmount)} to reserve the slot`
              : 'Select a slot to see pricing'}
          </div>
          <button
            className="selection-bar-btn"
            type="button"
            onClick={() => setShowBookingStep(true)}
            disabled={!selectedTheatre || !selectedSlot}
          >
            Add details, cakes, add-ons
          </button>
        </div>
      )}
      {showBookingStep && (
          <BookingStepOne
            onClose={() => setShowBookingStep(false)}
            theatre={selectedTheatre}
            selectedSlot={selectedSlot}
            showDate={formatDisplayDate(filters.show_date) || selectedTheatre?.raw?.slots_date || currentDate}
            bookingFee={selectedTheatre?.bookingFeeAmount || 0}
            advancePayableAmount={selectedTheatre?.advancePayableAmount || 0}
          />
        )}
      <div className="floating-buttons">
        <a href="tel:+918088397071" className="floating-btn call-btn" aria-label="Call us">
          <i className="bi bi-telephone-fill" />
        </a>
        <a
          href="https://wa.me/918088397071"
          className="floating-btn whatsapp-btn"
          target="_blank"
          rel="noreferrer"
          aria-label="WhatsApp us"
        >
          <i className="bi bi-whatsapp" />
        </a>
      </div>
    </div>
  );
};

export default HomeScreen;
